import { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  shouldFetchChart,
  getCachedChartData,
  saveCachedChartData,
} from './dashboardFetchCache';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import client from 'client';
import { connect } from 'react-redux';
import ManagemenpaymentMethods from '../account/ManagemenpaymentMethods';

// Generamos el array de fechas de la semana y su formato
const week = [
  moment().day('Sunday').format('MM/DD/YYYY'),
  moment().day('Monday').format('MM/DD/YYYY'),
  moment().day('Tuesday').format('MM/DD/YYYY'),
  moment().day('Wednesday').format('MM/DD/YYYY'),
  moment().day('Thursday').format('MM/DD/YYYY'),
  moment().day('Friday').format('MM/DD/YYYY'),
  moment().day('Saturday').format('MM/DD/YYYY'),
];

const formatDateArray = (dateArray) =>
  dateArray.map((dateStr) => moment(dateStr, 'MM/DD/YYYY').format('ddd MM/DD'));

const formattedArray = formatDateArray(week);

// Clave única para la semana actual (para cache)
const weekKey = `${moment(week[0]).format('YYYYMMDD')}_${moment(week[6]).format('YYYYMMDD')}`;

const formatNumber = (num) => {
  let formatted = num.toLocaleString();
  // Asegura que tenga dos decimales en caso de tener decimales
  if (formatted.includes('.') && formatted.split('.')[1].length < 2) {
    formatted += '0';
  }
  // Elimina separadores de miles si existen
  return formatted.replace(/,/g, '');
};

const CHART_COLORS = [
  '#008FFB', // Hauling Sales - Azul
  '#00E396', // Material Sales - Verde
  '#FEB019', // Wait Time Charges - Amarillo
  '#FF4560', // Fuel Costs - Rojo
  '#775DD0', // Employee Commissions - Púrpura
  '#3F51B5', // Owner Operator Commissions - Índigo
  '#D5EB33', // Fuel/Lube - Naranja vibrante
];

const MARKER_SIZE = 4;

/** Hide markers for 0/null so they don't sit on the X-axis under real values. */
function buildDiscreteMarkers(seriesData) {
  const discrete = [];
  seriesData.forEach((s, seriesIndex) => {
    (s.data || []).forEach((val, dataPointIndex) => {
      const n = Number(val);
      if (val == null || Number.isNaN(n) || n === 0) {
        discrete.push({
          seriesIndex,
          dataPointIndex,
          size: 0,
          fillColor: CHART_COLORS[seriesIndex % CHART_COLORS.length],
          strokeColor: '#fff',
        });
      }
    });
  });
  return discrete;
}

function ChartCompleted({ refreshGraph, compacct, user }) {
  const userId = user?.user_id;
  const compacctnum = user?.company?.compacctnum ?? compacct;

  const [series, setSeries] = useState([]);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);

  const chartOptions = {
    chart: {
      height: 350,
      type: 'area',
      offsetY: 0,
      toolbar: {
        show: false,
      },
      animations: {
        enabled: false,
      },
    },
    fill: {
      gradient: {},
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
    },
    colors: CHART_COLORS,
    xaxis: {
      categories: formattedArray,
      labels: {
        datetimeFormatter: {
          day: 'ddd MM/dd',
        },
      },
    },
    yaxis: {
      decimalsInFloat: 0,
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      followCursor: false,
      theme: 'light',
      style: {
        fontSize: '12px',
      },
      x: {
        show: true,
        formatter: (value, opts) => {
          const labels = opts?.w?.globals?.categoryLabels;
          const index = opts?.dataPointIndex;
          if (labels && index != null && labels[index] != null) {
            return String(labels[index]);
          }
          return value != null ? String(value) : '';
        },
      },
      y: {
        formatter: (value) => {
          if (value == null || Number.isNaN(Number(value))) return '';
          return `$${Number(value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        },
      },
    },
    legend: {
      show: true,
      offsetY: 10,
      onItemClick: {
        toggleDataSeries: true,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
      labels: {
        useSeriesColors: true,
      },
    },
    markers: {
      size: MARKER_SIZE,
      hover: {
        sizeOffset: 3,
      },
      discrete: buildDiscreteMarkers(series),
    },
  };

  const chartReadyKey =
    series.length > 0
      ? `ready-${series[0]?.data?.length ?? 0}`
      : 'empty';

  const PAYMENT_METHOD_SESSION_KEY = 'dashboard_payment_method_fetched';
  const paymentMethodRequestStartedRef = useRef(false);

  // Llamar a get-payment-method solo una vez por sesión al entrar al dashboard (evitar dobles por remount)
  useEffect(() => {
    if (!userId) return;
    const sessionKey = `${PAYMENT_METHOD_SESSION_KEY}_${userId}`;
    if (sessionStorage.getItem(sessionKey)) return;
    if (paymentMethodRequestStartedRef.current) return;

    paymentMethodRequestStartedRef.current = true;
    sessionStorage.setItem(sessionKey, '1');
    client
      .post('/payments/get-payment-method', {
        id: userId,
        payment_source: 1,
        all_method: true,
      })
      .then((response) => {
        const paymentData = response.data;
        if (
          !sessionStorage.getItem('managemenPaymentModalShown') &&
          paymentData &&
          paymentData.length === 0
        ) {
          setOpenPaymentModal(true);
          sessionStorage.setItem('managemenPaymentModalShown', 'true');
        }
      })
      .catch((err) => {
        console.error('Error fetching payment methods:', err);
        sessionStorage.removeItem(sessionKey);
      })
      .finally(() => {
        paymentMethodRequestStartedRef.current = false;
      });
  }, [userId]);

  // Ref para debounce de fetchChartData
  const fetchDebounceRef = useRef(null);
  const chartFetchInProgressRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const FETCH_CACHE_DURATION = 30000; // 30 segundos

  // Función para obtener y procesar los datos para el gráfico (depende de primitivos para evitar re-fetches por cambio de referencia de user)
  const fetchChartData = useCallback(async (forceRefresh = false) => {
    if (!userId || !compacctnum) return;

    // Evitar peticiones concurrentes (p. ej. efecto que corre dos veces antes de que la primera responda)
    if (chartFetchInProgressRef.current) return;

    // Verificar cache primero (a menos que sea refresh forzado)
    if (!forceRefresh) {
      const cachedData = getCachedChartData(userId, compacctnum, weekKey);
      if (cachedData) {
        console.log('📊 Using cached chart data');
        setSeries(cachedData);
        return;
      }
    }

    // Cancelar fetch anterior si existe
    if (fetchDebounceRef.current) {
      clearTimeout(fetchDebounceRef.current);
    }

    // Agregar debounce solo si no es refresh forzado
    const delay = forceRefresh ? 0 : 500;

    fetchDebounceRef.current = setTimeout(async () => {
      if (chartFetchInProgressRef.current) return;
      chartFetchInProgressRef.current = true;
      try {
        console.log('🔄 Fetching fresh chart data (unified endpoint)...');
        // Mejora 1: Una sola petición en lugar de 4
        const summaryResponse = await client.get(`/jobs/dashboard-graph-summary/${userId}/${compacctnum}`);
        const summaryData = summaryResponse.data;

        // Validación de la respuesta del gráfico
        const chartData = summaryData.chart;
        if (!chartData || chartData?.msg) {
          return;
        }

        // Arrays para los datos
        const materialCr = [];
        const ticketCr = [];
        const waitTimeCr = [];

        if (chartData && chartData.length > 0) {
          chartData.forEach((element) => {
            materialCr.push(element.materialCr || 0);
            ticketCr.push(element.ticketCr || 0);
            waitTimeCr.push(element.waitTime || 0);
          });
        }

        // Procesamos los datos de fuel, ticket y subconticket
        const fuelCosts = (summaryData.fuel?.day_totals || []).map((elem) => formatNumber(elem.total));
        const ticketCosts = (summaryData.ticketCost?.day_totals || []).map((elem) => elem.total);
        const subconticketCosts = (summaryData.subcon?.day_totals || []).map((elem) => elem.total);

        // Preparar datos de la serie
        const seriesData = [
          {
            name: 'Hauling Sales',
            data: ticketCr,
          },
          {
            name: 'Material Sales',
            data: materialCr,
          },
          {
            name: 'Wait Time Charges',
            data: waitTimeCr,
          },
          {
            name: 'Fuel Costs',
            data: fuelCosts,
          },
          {
            name: 'Employee Commissions',
            data: ticketCosts,
          },
          {
            name: 'Owner Operator Commissions',
            data: subconticketCosts.length ? subconticketCosts : [0],
          },
        ];

        // Guardar en cache
        saveCachedChartData(userId, compacctnum, weekKey, seriesData);

        // Actualizamos el estado
        setSeries(seriesData);
        lastFetchTimeRef.current = Date.now();
      } catch (error) {
        // Error fetching chart data - avoid console noise in production
        console.error('Error fetching chart data:', error);
      } finally {
        chartFetchInProgressRef.current = false;
      }
    }, delay);
  }, [userId, compacctnum]);

  const fetchChartDataRef = useRef(fetchChartData);
  fetchChartDataRef.current = fetchChartData;

  // Registrar en el ref para que el padre pueda llamar refresh (con refresh forzado)
  useEffect(() => {
    if (refreshGraph && !refreshGraph.current) {
      refreshGraph.current = () => fetchChartData(true); // true = force refresh
    }
  }, []);

  // Evitar volver a cargar solo por re-render (p. ej. resize): efecto solo cuando cambian userId o compacctnum
  const chartLoadAttemptedRef = useRef(false);

  // Cargar datos al montar (usa cache si está disponible). No volver a ejecutar por resize.
  useEffect(() => {
    if (!userId || !compacctnum) return;

    const cachedData = getCachedChartData(userId, compacctnum, weekKey);
    if (cachedData) {
      setSeries(cachedData);
      return;
    }

    if (chartLoadAttemptedRef.current) return;
    chartLoadAttemptedRef.current = true;

    if (shouldFetchChart(userId, compacctnum, weekKey)) {
      fetchChartDataRef.current?.();
    }

    return () => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
      }
    };
  }, [userId, compacctnum]);

  return (
    <>
      {openPaymentModal && (
        <ManagemenpaymentMethods open={openPaymentModal} onClose={setOpenPaymentModal} />
      )}
      <Card
        className='w-full rounded-none'
        style={{ border: '3px solid #edca33', backgroundColor: '#FFFFFF' }}
      >
        <div className='p-20 pb-0'>
          <Typography className='h2 font-Regular'>
            {moment(week[0]).format('MM/DD/YYYY')} to {moment(week[6]).format('MM/DD/YYYY')}
          </Typography>
          <div className='flex flex-row flex-wrap items-center mt-12'>
            <Typography className='text-48 font-Regular leading-none tracking-tighter' />
          </div>
        </div>
        <div className='h-300 w-100-p original-dashboard-apex-chart' style={{ overflow: 'visible', position: 'relative', zIndex: 2 }}>
          <ReactApexChart
            key={chartReadyKey}
            options={chartOptions}
            series={series}
            type='area'
            height={350}
          />
        </div>
      </Card>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.loadz_auth?.AuthReducer?.user,
});

export default memo(connect(mapStateToProps, null)(ChartCompleted));
